import { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Divider,
  TextField,
  List,
  ListItem,
  ListItemText,
  Alert,
  Checkbox,
  FormControlLabel,
} from '@mui/material';

import { formatCurrency, formatDate } from '../../../utils/formatUtils';
import {
  buildRowKey,
  getHeaderSubscribers,
  getSubscriberNameSummary,
} from '../utils/debtDetailUtils';
import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  DialogActions,
} from '../../../common/components';
const SendNotificationsDialog = ({
  open,
  onClose,
  onConfirm,
  selectedEntries = [],
  isSubmitting = false,
}) => {
  const [message, setMessage] = useState('');
  const [includeAmounts, setIncludeAmounts] = useState(false);

  useEffect(() => {
    if (!open) {
      setMessage('');
      setIncludeAmounts(false);
    }
  }, [open]);

  const totalCoupons = selectedEntries.reduce(
    (acc, entry) => acc + (entry.coupons?.length || 0),
    0
  );

  const totalAmount = selectedEntries.reduce((acc, entry) => {
    const headerTotal = Number(entry.header?.total || 0);
    return acc + headerTotal;
  }, 0);

  const recipientsKeys = selectedEntries.reduce((acc, entry) => {
    const header = entry.header || {};
    const subscribers = getHeaderSubscribers(header);

    if (subscribers.length > 0) {
      subscribers.forEach((subscriber) => {
        const key = subscriber.uuid
          ? `uuid:${subscriber.uuid}`
          : subscriber.email
          ? `email:${subscriber.email.toLowerCase()}`
          : subscriber.name
          ? `name:${subscriber.name.toLowerCase()}`
          : null;
        if (key) {
          acc.add(key);
        }
      });
    } else if (header.subscriber_email) {
      acc.add(`email:${header.subscriber_email.toLowerCase()}`);
    }

    return acc;
  }, new Set());

  const totalRecipients = recipientsKeys.size;

  return (
    <DialogContainer open={open} onClose={onClose} size="medium">
      <DialogTitle>Confirmar envío de notificaciones</DialogTitle>
      <DialogBody sx={{ pt: 3 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Se enviará un correo personalizado a cada suscriptor incluido en la lista utilizando la
          plantilla de recordatorio de mora corporativa. Podés agregar un mensaje complementario que
          se incluirá en el cuerpo del email.
        </Alert>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Resumen del envío
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {totalRecipients} suscriptores • {selectedEntries.length} contratos • {totalCoupons} cupones •{' '}
            {formatCurrency(totalAmount)}
          </Typography>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Checkbox
                checked={includeAmounts}
                onChange={(event) => setIncludeAmounts(event.target.checked)}
                color="primary"
              />
            }
            label="Enviar montos"
          />
        </Box>

        <TextField
          label="Mensaje adicional (opcional)"
          multiline
          minRows={3}
          fullWidth
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          placeholder="Ej: Recordá que podés abonar tus cuotas en nuestras oficinas o a través de los medios habituales."
          sx={{ mb: 3 }}
        />

        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          Suscriptores incluidos
        </Typography>

        <List dense sx={{ maxHeight: 240, overflowY: 'auto' }}>
          {selectedEntries.map((entry) => {
            const header = entry.header || {};
            const rowKey = buildRowKey(header);
            const subscribers = getHeaderSubscribers(header);
            const subscriberSummary = getSubscriberNameSummary(header);

            const dueLabel = header.closest_due_date
              ? `${formatDate(header.closest_due_date)} (${header.days_until_due ?? '-'} días)`
              : 'Sin información';

            const subscriberLines =
              subscribers.length > 0
                ? subscribers.map((subscriber, index) => ({
                    id: subscriber.uuid || `${rowKey}-subscriber-${index}`,
                    label: subscriber.email
                      ? `${subscriber.name || 'Sin nombre'} — ${subscriber.email}`
                      : subscriber.name || 'Sin nombre',
                  }))
                : [
                    {
                      id: `${rowKey}-no-subscribers`,
                      label: 'Sin suscriptores asociados',
                    },
                  ];

            return (
              <ListItem key={rowKey} alignItems="flex-start" sx={{ alignItems: 'baseline' }}>
                <ListItemText
                  primary={
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle2" fontWeight={600}>
                        {header.contract_number} • {subscriberSummary}
                      </Typography>
                      <Typography variant="subtitle2" color="primary">
                        {formatCurrency(header.total ?? 0)}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <>
                      {subscriberLines.map((subscriberLine) => (
                        <Typography key={subscriberLine.id} variant="body2" color="text.secondary">
                          {subscriberLine.label}
                        </Typography>
                      ))}
                      <Typography variant="body2" color="text.secondary">
                        Cuotas: {entry.coupons?.length || 0} • Próximo vencimiento: {dueLabel}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
            );
          })}
        </List>
      </DialogBody>
      <DialogActions
        handleClose={onClose}
        handleSave={() => onConfirm({ message, includeAmounts })}
        saveLabel="Confirmar envío"
        savingLabel="Enviando..."
        cancelLabel="Cancelar"
        saveButtonProps={{
          isLoading: isSubmitting,
          disabled: isSubmitting || selectedEntries.length === 0,
        }}
        cancelButtonProps={{
          disabled: isSubmitting,
        }}
      />
    </DialogContainer>
  );
};
 
export default SendNotificationsDialog;
